import type { ReactNode } from 'react';
import { TAILLES_PAGE_DISPONIBLES, type TaillePage } from '../../lib/useTaillePage';

/**
 * Briques d'interface reprises de la maquette validée
 * (`docs/maquette/modules/08-pole-administratif-commercial.js`, helpers
 * `ph`, `k6`, `c6`, `bdg`, `btnP`, `btnS`, `fileRow`, `alertRow`).
 * Les classes sont recopiées à l'identique : ne pas les retoucher sans
 * validation client.
 */

export function EnteteEcran({
  titre,
  sousTitre,
  actions,
}: {
  titre: string;
  sousTitre: string;
  actions?: ReactNode;
}): JSX.Element {
  return (
    <div className="flex flex-col items-start justify-between gap-4 lg:flex-row lg:items-center">
      <div>
        <h1 className="font-display text-2xl font-bold text-codialis-navy">{titre}</h1>
        <p className="mt-0.5 text-sm text-codialis-gray">{sousTitre}</p>
      </div>
      <div className="flex flex-wrap items-center gap-2">{actions}</div>
    </div>
  );
}

const TONS_COMPTEUR = {
  mint: 'text-codialis-teal',
  blue: 'text-codialis-sky',
  warm: 'text-amber-600',
  red: 'text-red-600',
  navy: 'text-codialis-navy',
} as const;

export function Compteur({
  libelle,
  valeur,
  detail,
  ton = 'navy',
}: {
  libelle: string;
  valeur: string | number;
  detail?: string;
  ton?: keyof typeof TONS_COMPTEUR;
}): JSX.Element {
  return (
    <div className="stat-card rounded-2xl bg-white p-4 sm:p-5 shadow-card">
      <div className="text-[11px] font-bold uppercase tracking-wider text-codialis-gray">
        {libelle}
      </div>
      <div className={`font-display mt-1 text-3xl font-extrabold ${TONS_COMPTEUR[ton]}`}>
        {valeur}
      </div>
      {detail && <div className="mt-0.5 text-xs text-codialis-gray">{detail}</div>}
    </div>
  );
}

export function Carte({
  titre,
  actions,
  children,
  sansBordure = false,
}: {
  titre: string;
  actions?: ReactNode;
  children: ReactNode;
  sansBordure?: boolean;
}): JSX.Element {
  return (
    <div className="overflow-hidden rounded-2xl bg-white shadow-card">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 px-4 py-3.5 sm:px-6 sm:py-4">
        {/* `h2` sous le `h1` de l'écran (RGAA 9.1) : les classes font la taille. */}
        <h2 className="font-display min-w-0 font-bold text-codialis-navy">{titre}</h2>
        <div className="flex flex-wrap items-center gap-2">{actions}</div>
      </div>
      <div className={sansBordure ? '' : 'divide-y divide-gray-100'}>{children}</div>
    </div>
  );
}

export type TonPastille =
  | 'success' | 'warning' | 'danger' | 'info' | 'neutral' | 'mint';

export function Pastille({
  texte,
  ton = 'neutral',
}: {
  texte: string;
  ton?: TonPastille | string;
}): JSX.Element {
  return (
    <span
      className={`badge-${ton} whitespace-nowrap rounded px-2 py-0.5 text-[10px] font-bold`}
    >
      {texte}
    </span>
  );
}

export function BoutonPrincipal({
  children,
  onClick,
  type = 'button',
  disabled,
}: {
  children: ReactNode;
  onClick?: () => void;
  type?: 'button' | 'submit';
  disabled?: boolean;
}): JSX.Element {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className="inline-flex min-h-[44px] items-center justify-center rounded-xl bg-codialis-navy px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-codialis-deep disabled:cursor-not-allowed disabled:opacity-60"
    >
      {children}
    </button>
  );
}

export function BoutonSecondaire({
  children,
  onClick,
  type = 'button',
  disabled,
}: {
  children: ReactNode;
  onClick?: () => void;
  type?: 'button' | 'submit';
  disabled?: boolean;
}): JSX.Element {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className="inline-flex min-h-[44px] items-center justify-center rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-codialis-navy transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
    >
      {children}
    </button>
  );
}

/** Bouton d'action mint, comme `btnM` dans la maquette. */
export function BoutonMint({
  children,
  onClick,
  type = 'button',
  disabled,
}: {
  children: ReactNode;
  onClick?: () => void;
  type?: 'button' | 'submit';
  disabled?: boolean;
}): JSX.Element {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className="gradient-mint inline-flex min-h-[44px] items-center justify-center rounded-xl px-4 py-2.5 text-sm font-semibold text-codialis-navy transition hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-60"
    >
      {children}
    </button>
  );
}

/** Ligne de pièce justificative, comme `fileRow` dans la maquette. */
export function LigneFichier({
  nom,
  detail,
  pastille,
  actions,
}: {
  nom: string;
  detail: string;
  pastille: { texte: string; ton: TonPastille };
  actions?: ReactNode;
}): JSX.Element {
  const manquante = pastille.ton === 'danger';
  return (
    <div className="flex flex-wrap items-center gap-3 px-4 py-4 transition hover:bg-gray-50 sm:flex-nowrap sm:gap-4 sm:px-6">
      <svg aria-hidden="true"
        className={`h-7 w-7 shrink-0 ${manquante ? 'text-red-400' : 'text-codialis-teal'}`}
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
        />
      </svg>
      <div className="min-w-[10rem] flex-1 basis-40">
        <div className="truncate text-sm font-medium text-codialis-navy">{nom}</div>
        <div className="text-[11px] text-codialis-gray">{detail}</div>
      </div>
      {actions}
      <Pastille texte={pastille.texte} ton={pastille.ton} />
    </div>
  );
}

const PALETTES_SITE = [
  'bg-codialis-sky/15 text-codialis-navy',
  'bg-codialis-mint/30 text-codialis-navy',
  'bg-purple-100 text-purple-900',
  'bg-amber-100 text-amber-900',
  'bg-emerald-100 text-emerald-900',
  'bg-rose-100 text-rose-900',
  'bg-indigo-100 text-indigo-900',
];

export function couleurPastilleSite(site: string): string {
  let hash = 0;
  for (let i = 0; i < site.length; i++) {
    hash = (hash << 5) - hash + site.charCodeAt(i);
  }
  const index = Math.abs(hash) % PALETTES_SITE.length;
  return PALETTES_SITE[index]!;
}

export function PastilleSite({ site }: { site?: string }): JSX.Element | null {
  if (!site || site === 'Tous les sites' || site === '-') return null;
  return (
    <span
      className={`rounded px-1.5 py-0.5 font-mono text-[10px] font-bold ${couleurPastilleSite(site)}`}
    >
      {site}
    </span>
  );
}

/** Ligne d'alerte, comme `alertRow` dans la maquette. */
export function LigneAlerte({
  titre,
  detail,
  ton = 'info',
  site,
}: {
  titre: string;
  detail: string;
  ton?: 'danger' | 'warning' | 'info';
  site?: string;
}): JSX.Element {
  const puce =
    ton === 'danger' ? 'bg-red-500' : ton === 'warning' ? 'bg-amber-500' : 'bg-codialis-sky';
  return (
    <div className="flex items-start gap-3 px-4 py-3.5 transition hover:bg-gray-50 sm:px-6">
      <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${puce}`} />
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-codialis-navy">{titre}</div>
        <div className="mt-0.5 text-xs text-codialis-gray">{detail}</div>
      </div>
      {site && <PastilleSite site={site} />}
    </div>
  );
}

/**
 * Nombre d'alertes affichées dans la carte « Mes alertes » d'un tableau de bord.
 * Au-delà, le reste est renvoyé vers le module d'alertes : la carte reste lisible
 * sur tablette sans allonger la colonne de droite indéfiniment.
 */
export const MAX_ALERTES_TABLEAU_BORD = 5;

export function PiedAlertes({
  restantes,
  onClick,
}: {
  restantes: number;
  onClick: () => void;
}): JSX.Element | null {
  if (restantes <= 0) return null;
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full px-4 py-3 text-left text-xs font-bold text-codialis-sky transition hover:bg-gray-50 sm:px-6"
    >
      {restantes === 1
        ? 'Voir 1 autre alerte'
        : `Voir les ${restantes} autres alertes`}
    </button>
  );
}

/**
 * Astérisque d'un champ obligatoire (RGAA 11.10, TF-341) : visible, et lu
 * « obligatoire » par les lecteurs d'écran, qui ne prononcent pas l'étoile.
 */
export function MarqueObligatoire({ className = 'ml-0.5 text-codialis-sky' }: { className?: string }): JSX.Element {
  return (
    <>
      <span aria-hidden="true" className={className}>
        *
      </span>
      <span className="sr-only"> (obligatoire)</span>
    </>
  );
}

export function Chargement({ libelle = 'Chargement…' }: { libelle?: string }): JSX.Element {
  return <div className="p-4 sm:p-6 text-sm text-codialis-gray">{libelle}</div>;
}

export function MessageErreur({ message }: { message: string }): JSX.Element {
  return (
    <div
      role="alert"
      className="rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700"
    >
      {message}
    </div>
  );
}

/**
 * Écran qui ne peut pas s'afficher — refus de l'API, erreur de lecture : son
 * titre reste en tête de page (RGAA 9.1, TF-341) et le message dit pourquoi.
 */
export function EcranEnErreur({ titre, message }: { titre: string; message: string }): JSX.Element {
  return (
    <div className="mx-auto max-w-[1500px] space-y-4 p-4 sm:p-6">
      <h1 className="font-display text-2xl font-bold text-codialis-navy">{titre}</h1>
      <MessageErreur message={message} />
    </div>
  );
}

export function BoutonPage({
  children,
  onClick,
  desactive,
  libelle,
}: {
  children: ReactNode;
  onClick: () => void;
  desactive?: boolean;
  libelle: string;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={desactive}
      aria-label={libelle}
      className="min-h-[36px] [@media(pointer:coarse)]:min-h-[40px] min-w-[36px] [@media(pointer:coarse)]:min-w-[40px] rounded text-xs font-semibold text-codialis-gray transition hover:bg-white hover:text-codialis-navy disabled:cursor-not-allowed disabled:opacity-40"
    >
      {children}
    </button>
  );
}

export interface PropsPagination {
  page: number;
  pages: number;
  total: number;
  affiches: number;
  taillePage?: TaillePage;
  onPage: (page: number) => void;
  onChangerTaille?: (taille: TaillePage) => void;
  uniteLabel?: string;
}

/**
 * Composant de pagination réutilisable conforme à la charte graphique Codialis.
 * Inclut le résumé des effectifs, le sélecteur de taille (15, 25, 50, 100) avec
 * sauvegarde de la préférence utilisateur, et les boutons de navigation.
 */
export function Pagination({
  page,
  pages,
  total,
  affiches,
  taillePage = 25,
  onPage,
  onChangerTaille,
  uniteLabel = 'élément',
}: PropsPagination): JSX.Element {
  const premier = Math.max(1, (page - 1) * taillePage + 1);
  const dernier = Math.min(total, premier + affiches - 1);
  const debut = Math.max(1, Math.min(page - 2, pages - 4));
  const numeros = Array.from({ length: Math.min(5, pages) }, (_, index) => debut + index);

  return (
    <div className="flex flex-col items-center justify-between gap-3 border-t border-gray-100 bg-gray-50 px-4 py-3.5 text-sm sm:gap-4 sm:px-5 sm:flex-row">
      {/* Effectif affiché */}
      <span className="text-xs text-codialis-gray sm:text-sm">
        {total === 0 ? (
          `0 ${uniteLabel}`
        ) : affiches === total ? (
          `${total} ${uniteLabel}${total > 1 ? 's' : ''}`
        ) : (
          `${premier}–${dernier} sur ${total} ${uniteLabel}${total > 1 ? 's' : ''}`
        )}
      </span>

      {/* Sélecteur de pagination 15 / 25 / 50 / 100 avec sauvegarde des préférences */}
      {onChangerTaille && (
        <div className="flex items-center gap-2 text-xs text-codialis-gray">
          <span className="hidden sm:inline">Afficher par :</span>
          <div className="inline-flex rounded-lg border border-gray-200 bg-white p-0.5 shadow-xs">
            {TAILLES_PAGE_DISPONIBLES.map((taille) => (
              <button
                key={taille}
                type="button"
                onClick={() => onChangerTaille(taille)}
                aria-pressed={taillePage === taille}
                className={`rounded px-2.5 py-1 text-xs font-semibold transition ${
                  taillePage === taille
                    ? 'bg-codialis-navy text-white shadow-xs'
                    : 'text-codialis-gray hover:bg-gray-100 hover:text-codialis-navy'
                }`}
              >
                {taille}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Boutons de navigation de pages */}
      {pages > 1 && (
        <div className="flex flex-wrap items-center justify-center gap-1">
          <BoutonPage
            libelle="Page précédente"
            desactive={page <= 1}
            onClick={() => onPage(page - 1)}
          >
            ‹
          </BoutonPage>
          {numeros[0]! > 1 && <span className="px-1 text-codialis-gray">…</span>}
          {numeros.map((numero) => (
            <button
              key={numero}
              type="button"
              onClick={() => onPage(numero)}
              aria-current={numero === page ? 'page' : undefined}
              className={`min-h-[36px] [@media(pointer:coarse)]:min-h-[40px] min-w-[36px] [@media(pointer:coarse)]:min-w-[40px] rounded text-xs font-semibold transition ${
                numero === page
                  ? 'bg-codialis-navy text-white shadow-xs'
                  : 'text-codialis-gray hover:bg-white hover:text-codialis-navy'
              }`}
            >
              {numero}
            </button>
          ))}
          {numeros[numeros.length - 1]! < pages && (
            <span className="px-1 text-codialis-gray">…</span>
          )}
          <BoutonPage
            libelle="Page suivante"
            desactive={page >= pages}
            onClick={() => onPage(page + 1)}
          >
            ›
          </BoutonPage>
        </div>
      )}
    </div>
  );
}

